import { FC, useMemo } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { Field, FieldArray, FormikProvider, useFormik } from 'formik';
import { Plus, Trash2 } from 'lucide-react';
import { isEmpty, isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { TimePicker } from '@mui/x-date-pickers';

import { SCHEDULE_BALANCE, ScheduleBalance, ScheduleBalanceForm } from './form';
import { formatTimeToPicker, mapFormToDto } from './utils';

import css from './ScheduleBalanceDistributionDialog.module.css';

import { schedulerService } from 'v2/api/schedulerService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { Loader } from 'v2/components/loader/Loader';
import { BalanceJobType, USER_ROLES } from 'v2/constants';
import { enumToSelect, handleHttpError } from 'v2/utils';

interface ScheduleBalanceDistributionDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

export const ScheduleBalanceDistributionDialog: FC<ScheduleBalanceDistributionDialogProps> = ({ isOpen, onClose }) => {
  const { data, isLoading, mutate } = useSWR(isOpen && 'getBalanceSchedules', schedulerService.getBalanceSchedules);

  const initialValues = useMemo(() => {
    if (isNullish(data)) return [];

    return data.map((schedule) => ({
      ...schedule,
      time: formatTimeToPicker(schedule.time),
    }));
  }, [data]);

  const formik = useFormik<ScheduleBalanceForm>({
    initialValues: { schedules: initialValues },
    onSubmit: (values) => {
      triggerUpdate(mapFormToDto(values));
    },
    enableReinitialize: true,
  });

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    'updateBalanceSchedules',
    schedulerService.updateBalanceSchedules,
    {
      onSuccess: () => {
        toast.success('Schedules have been saved');
        onClose();
        formik.resetForm();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation('deleteBalanceSchedule', schedulerService.deleteBalanceSchedule, {
    onSuccess: () => {
      mutate();
    },
    onError: handleHttpError,
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Schedule Balance Distribution</DialogTitle>

      <DialogContent className={css.scheduleBalanceDialogContent}>
        <form id="scheduleBalanceForm" onSubmit={formik.handleSubmit}>
          {isLoading ? (
            <Loader />
          ) : (
            <FormikProvider value={formik}>
              <FieldArray name="schedules">
                {({ push, remove, form }) => (
                  <div className="form">
                    <Button onClick={() => push(SCHEDULE_BALANCE)} startIcon={<Plus />} variant="outlined" size="large">
                      Schedule
                    </Button>

                    {form.values.schedules.map((schedule: ScheduleBalance, i: number) => (
                      <div key={i} className={css.scheduleBalanceCard}>
                        <div className={css.scheduleBalanceCardActions}>
                          <Chip
                            label={schedule.isEnabled ? 'Enabled' : 'Disabled'}
                            color={schedule.isEnabled ? 'success' : 'default'}
                            size="small"
                            onClick={() => formik.setFieldValue(`schedules.${i}.isEnabled`, !schedule.isEnabled)}
                          />

                          <IconButton
                            color="error"
                            onClick={() => {
                              if (!isEmpty(schedule.id)) {
                                triggerDelete(schedule.id);
                              }

                              remove(i);
                            }}
                          >
                            <Trash2 width={18} height={18} />
                          </IconButton>
                        </div>

                        <div className="form">
                          <div className={clsx('form-group', css.scheduleBalanceFormGroup)}>
                            <TimePicker
                              label="Schedule Time *"
                              value={dayjs(schedule.time)}
                              onChange={(newValue) => {
                                formik.setFieldValue(`schedules.${i}.time`, newValue);
                              }}
                              format="HH:mm"
                            />

                            <FormControl required fullWidth>
                              <InputLabel id={i.toString()}>Schedule Type</InputLabel>
                              <Field
                                as={Select}
                                name={`schedules.${i}.type`}
                                labelId={i.toString()}
                                label="Schedule Type"
                              >
                                {enumToSelect(BalanceJobType).map((option) => (
                                  <MenuItem key={option.id} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </div>

                          <Field
                            as={TextField}
                            name={`schedules.${i}.balance`}
                            label="Balance"
                            type="number"
                            fullWidth
                            required
                          />

                          <CheckBoxAutocomplete
                            label="Roles"
                            value={schedule.roles}
                            options={USER_ROLES}
                            onChange={(v) => formik.setFieldValue(`schedules.${i}.roles`, v)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>
            </FormikProvider>
          )}
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="scheduleBalanceForm" loading={isMutating} type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
